import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import { 
  Plus,
  X,
  Trash2,
  Loader2,
  GripVertical,
  FileX,
  Edit,
  MessageSquare,
  Users,
  Send,
  ArrowRight,
  User,
  Clock
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default columns
const DEFAULT_COLUMNS = [
  { id: "not_planned", title: "Не спланированы", color: "#0ea5e9" },
  { id: "this_week", title: "Сделаю на неделе", color: "#22c55e" },
  { id: "in_progress", title: "В работе", color: "#eab308" },
  { id: "done", title: "Готово", color: "#6b7280" }
];

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [columns, setColumns] = useState(() => {
    const saved = localStorage.getItem("kanban_columns");
    return saved ? JSON.parse(saved) : DEFAULT_COLUMNS;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [newColumnTitle, setNewColumnTitle] = useState("");
  const [showNewColumn, setShowNewColumn] = useState(false);
  const [quickTaskColumn, setQuickTaskColumn] = useState(null);
  const [quickTaskTitle, setQuickTaskTitle] = useState("");
  
  // Modal states
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [newComment, setNewComment] = useState("");
  const [transferReason, setTransferReason] = useState("");
  const [transferToUser, setTransferToUser] = useState("");
  
  // New task form
  const [newTask, setNewTask] = useState({
    title: "",
    description: "",
    status: "not_planned",
    assignees: [],
    isShared: false
  });

  const currentUser = localStorage.getItem("username") || "admin";

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchTasks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tasks`, {
        headers: getAuthHeaders(),
      });
      setTasks(response.data);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Ошибка загрузки задач");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: getAuthHeaders(),
      });
      setUsers(response.data || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, []);

  useEffect(() => {
    fetchTasks();
    fetchUsers();
  }, [fetchTasks, fetchUsers]);

  // Save columns to localStorage
  useEffect(() => {
    localStorage.setItem("kanban_columns", JSON.stringify(columns));
  }, [columns]);

  const handleAddColumn = () => {
    if (!newColumnTitle.trim()) {
      toast.error("Введите название столбца");
      return;
    }
    
    const colors = ["#0ea5e9", "#22c55e", "#eab308", "#ef4444", "#8b5cf6", "#ec4899", "#f97316"];
    const newColumn = {
      id: `col_${Date.now()}`,
      title: newColumnTitle.trim(),
      color: colors[columns.length % colors.length]
    };
    
    setColumns([...columns, newColumn]);
    setNewColumnTitle("");
    setShowNewColumn(false);
    toast.success("Столбец добавлен");
  };

  const handleDeleteColumn = (columnId) => {
    if (columns.length <= 1) {
      toast.error("Нельзя удалить последний столбец");
      return;
    }
    
    const tasksToMove = tasks.filter(t => t.status === columnId);
    if (tasksToMove.length > 0) {
      tasksToMove.forEach(async (task) => {
        await handleMoveTask(task.id, columns[0].id);
      });
    }
    
    setColumns(columns.filter(c => c.id !== columnId));
    toast.success("Столбец удалён");
  };

  const handleQuickTask = async (columnId) => {
    if (!quickTaskTitle.trim()) {
      toast.error("Введите название задачи");
      return;
    }
    
    try {
      await axios.post(
        `${API}/tasks`,
        { 
          title: quickTaskTitle.trim(),
          description: "",
          status: columnId,
          assignees: [currentUser],
          comments: [],
          created_by: currentUser
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Задача создана");
      setQuickTaskTitle("");
      setQuickTaskColumn(null);
      fetchTasks();
    } catch (error) {
      toast.error("Ошибка создания задачи");
    }
  };

  // Create new task with modal
  const handleCreateTask = async () => {
    if (!newTask.title.trim()) {
      toast.error("Введите название задачи");
      return;
    }
    
    try {
      const taskData = {
        title: newTask.title.trim(),
        description: newTask.description,
        status: newTask.status,
        assignees: newTask.isShared ? newTask.assignees : [currentUser],
        is_shared: newTask.isShared,
        comments: [],
        created_by: currentUser,
        created_at: new Date().toISOString()
      };
      
      await axios.post(`${API}/tasks`, taskData, { headers: getAuthHeaders() });
      toast.success("Задача создана");
      setShowTaskModal(false);
      setNewTask({ title: "", description: "", status: "not_planned", assignees: [], isShared: false });
      fetchTasks();
    } catch (error) {
      toast.error("Ошибка создания задачи");
    }
  };

  // Edit task
  const handleEditTask = async () => {
    if (!selectedTask) return;
    
    try {
      await axios.patch(
        `${API}/tasks/${selectedTask.id}`,
        {
          title: selectedTask.title,
          description: selectedTask.description,
          assignees: selectedTask.assignees
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Задача обновлена");
      setShowEditModal(false);
      fetchTasks();
    } catch (error) {
      toast.error("Ошибка обновления задачи");
    }
  };

  // Add comment
  const handleAddComment = async () => {
    if (!selectedTask || !newComment.trim()) return;
    
    const comment = {
      id: Date.now().toString(),
      text: newComment.trim(),
      author: currentUser,
      created_at: new Date().toISOString()
    };
    
    const updatedComments = [...(selectedTask.comments || []), comment];
    
    try {
      await axios.patch(
        `${API}/tasks/${selectedTask.id}`,
        { comments: updatedComments },
        { headers: getAuthHeaders() }
      );
      setSelectedTask({ ...selectedTask, comments: updatedComments });
      setNewComment("");
      toast.success("Комментарий добавлен");
      fetchTasks();
    } catch (error) {
      toast.error("Ошибка добавления комментария");
    }
  };

  // Transfer task
  const handleTransferTask = async () => {
    if (!selectedTask || !transferToUser) {
      toast.error("Выберите пользователя");
      return;
    }
    
    const transferComment = {
      id: Date.now().toString(),
      text: `📤 Задача передана пользователю ${transferToUser}. Причина: ${transferReason || "не указана"}`,
      author: currentUser,
      created_at: new Date().toISOString(),
      isTransfer: true
    };
    
    const updatedComments = [...(selectedTask.comments || []), transferComment];
    const updatedAssignees = selectedTask.assignees?.includes(transferToUser) 
      ? selectedTask.assignees 
      : [...(selectedTask.assignees || []), transferToUser];
    
    try {
      await axios.patch(
        `${API}/tasks/${selectedTask.id}`,
        { 
          assignees: updatedAssignees,
          comments: updatedComments,
          transferred_to: transferToUser,
          transferred_by: currentUser,
          transfer_reason: transferReason
        },
        { headers: getAuthHeaders() }
      );
      toast.success(`Задача передана пользователю ${transferToUser}`);
      setShowTransferModal(false);
      setTransferReason("");
      setTransferToUser("");
      fetchTasks();
    } catch (error) {
      toast.error("Ошибка передачи задачи");
    }
  };

  const handleMoveTask = async (taskId, newStatus) => {
    try {
      await axios.patch(
        `${API}/tasks/${taskId}`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      fetchTasks();
    } catch (error) {
      toast.error("Ошибка перемещения задачи");
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Удалить задачу?")) return;
    try {
      await axios.delete(`${API}/tasks/${taskId}`, { headers: getAuthHeaders() });
      toast.success("Задача удалена");
      fetchTasks();
    } catch (error) {
      toast.error("Ошибка удаления задачи");
    }
  };

  const getTasksForColumn = (columnId) => {
    return tasks.filter(t => t.status === columnId);
  };

  const openTaskDetails = (task) => {
    setSelectedTask(task);
    setShowEditModal(true);
  };

  const openTransferModal = (task) => {
    setSelectedTask(task);
    setShowTransferModal(true);
  };

  const toggleAssignee = (username) => {
    const current = newTask.assignees || [];
    if (current.includes(username)) {
      setNewTask({ ...newTask, assignees: current.filter(u => u !== username) });
    } else {
      setNewTask({ ...newTask, assignees: [...current, username] });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[80vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col overflow-hidden -m-6" data-testid="tasks-page">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 bg-white border-b">
        <h1 className="text-xl font-bold text-slate-800">Задачи</h1>
        <Button onClick={() => setShowTaskModal(true)} className="gap-2">
          <Plus className="w-4 h-4" />
          Создать задачу
        </Button>
      </div>

      {/* Kanban Board */}
      <div 
        className="flex-1 overflow-hidden min-w-0"
        style={{
          background: "linear-gradient(135deg, #4a90a4 0%, #5ba3b5 50%, #6bb3c5 100%)",
        }}
      >
        <div className="flex gap-4 p-4 h-full overflow-x-auto overflow-y-hidden">
          {/* Columns */}
          {columns.map((column) => {
            const columnTasks = getTasksForColumn(column.id);
            
            return (
              <div 
                key={column.id} 
                className="flex-shrink-0 w-72 flex flex-col max-h-full"
              >
                {/* Column Header */}
                <div 
                  className="rounded-t-lg px-4 py-3 flex items-center justify-between flex-shrink-0"
                  style={{ backgroundColor: column.color }}
                >
                  <span className="text-white font-semibold text-sm">
                    {column.title} ({columnTasks.length})
                  </span>
                  {columns.length > 1 && (
                    <button
                      onClick={() => handleDeleteColumn(column.id)}
                      className="text-white/70 hover:text-white transition-colors"
                      title="Удалить столбец"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                
                {/* Add Task Button */}
                <div className="bg-white/30 backdrop-blur-sm border-x border-white/30 flex-shrink-0">
                  {quickTaskColumn === column.id ? (
                    <div className="p-2">
                      <Input
                        value={quickTaskTitle}
                        onChange={(e) => setQuickTaskTitle(e.target.value)}
                        placeholder="Название задачи..."
                        className="mb-2 bg-white"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") handleQuickTask(column.id);
                          if (e.key === "Escape") setQuickTaskColumn(null);
                        }}
                      />
                      <div className="flex gap-2">
                        <Button 
                          size="sm" 
                          onClick={() => handleQuickTask(column.id)}
                          className="flex-1"
                        >
                          Добавить
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => setQuickTaskColumn(null)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setQuickTaskColumn(column.id)}
                      className="w-full py-2 text-white/80 hover:text-white hover:bg-white/10 transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Быстрая задача</span>
                    </button>
                  )}
                </div>
                
                {/* Tasks List */}
                <div className="flex-1 overflow-y-auto bg-white/5 backdrop-blur-sm border-x border-b border-white/20 rounded-b-lg p-2 space-y-2 min-h-0">
                  {columnTasks.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-8 text-white/50">
                      <FileX className="w-12 h-12 mb-2 opacity-50" />
                      <p className="text-sm text-center">Пока данных нет</p>
                    </div>
                  ) : (
                    columnTasks.map((task) => (
                      <div
                        key={task.id}
                        className="bg-white rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow group cursor-pointer"
                        onClick={() => openTaskDetails(task)}
                      >
                        <div className="flex items-start gap-2">
                          <GripVertical className="w-4 h-4 text-slate-300 mt-1 cursor-grab flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-slate-800 text-sm truncate">
                              {task.title}
                            </h4>
                            {task.description && (
                              <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                                {task.description}
                              </p>
                            )}
                            
                            {/* Assignees */}
                            {task.assignees?.length > 0 && (
                              <div className="flex items-center gap-1 mt-2 flex-wrap">
                                {task.is_shared && <Users className="w-3 h-3 text-blue-500" />}
                                {task.assignees.slice(0, 3).map((assignee, idx) => (
                                  <span key={idx} className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                    {assignee}
                                  </span>
                                ))}
                                {task.assignees.length > 3 && (
                                  <span className="text-xs text-slate-500">+{task.assignees.length - 3}</span>
                                )}
                              </div>
                            )}
                            
                            {/* Comments count */}
                            {task.comments?.length > 0 && (
                              <div className="flex items-center gap-1 mt-2 text-slate-400 text-xs">
                                <MessageSquare className="w-3 h-3" />
                                {task.comments.length}
                              </div>
                            )}
                          </div>
                          <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => { e.stopPropagation(); openTransferModal(task); }}
                              className="text-slate-400 hover:text-blue-500"
                              title="Передать"
                            >
                              <ArrowRight className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleDeleteTask(task.id); }}
                              className="text-slate-400 hover:text-red-500"
                              title="Удалить"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                        
                        {/* Move buttons */}
                        <div className="mt-2 pt-2 border-t border-slate-100 flex gap-1 flex-wrap">
                          {columns.filter(c => c.id !== task.status).slice(0, 3).map((col) => (
                            <button
                              key={col.id}
                              onClick={(e) => { e.stopPropagation(); handleMoveTask(task.id, col.id); }}
                              className="text-xs px-2 py-1 rounded hover:opacity-80 transition-opacity text-white truncate max-w-[80px]"
                              style={{ backgroundColor: col.color }}
                              title={col.title}
                            >
                              → {col.title.slice(0, 8)}
                            </button>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}

          {/* Add New Column */}
          <div className="flex-shrink-0 w-72">
            {showNewColumn ? (
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                <Input
                  value={newColumnTitle}
                  onChange={(e) => setNewColumnTitle(e.target.value)}
                  placeholder="Название столбца..."
                  className="mb-2 bg-white"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleAddColumn();
                    if (e.key === "Escape") setShowNewColumn(false);
                  }}
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleAddColumn} className="flex-1 bg-orange-500 hover:bg-orange-600">
                    Добавить
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setShowNewColumn(false)} className="text-white">
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => setShowNewColumn(true)}
                className="w-full h-12 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold flex items-center justify-center gap-2 transition-colors"
              >
                <Plus className="w-5 h-5" />
                Новый столбец
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Create Task Modal */}
      <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Создать задачу</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Название</Label>
              <Input
                value={newTask.title}
                onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                placeholder="Введите название задачи..."
              />
            </div>
            <div>
              <Label>Описание</Label>
              <Textarea
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                placeholder="Описание задачи..."
                rows={3}
              />
            </div>
            <div>
              <Label>Статус</Label>
              <select
                value={newTask.status}
                onChange={(e) => setNewTask({ ...newTask, status: e.target.value })}
                className="w-full p-2 border rounded-lg"
              >
                {columns.map(col => (
                  <option key={col.id} value={col.id}>{col.title}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={newTask.isShared}
                  onChange={(e) => setNewTask({ ...newTask, isShared: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Общая задача (несколько исполнителей)</span>
              </label>
            </div>
            {newTask.isShared && (
              <div>
                <Label>Исполнители</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {users.map(user => (
                    <button
                      key={user.username}
                      type="button"
                      onClick={() => toggleAssignee(user.username)}
                      className={`px-3 py-1 rounded-full text-sm transition-colors ${
                        newTask.assignees?.includes(user.username)
                          ? 'bg-blue-500 text-white'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {user.username}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-2 pt-4">
              <Button onClick={handleCreateTask} className="flex-1">
                Создать
              </Button>
              <Button variant="outline" onClick={() => setShowTaskModal(false)}>
                Отмена
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Task / Comments Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Редактировать задачу</DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-4">
              <div>
                <Label>Название</Label>
                <Input
                  value={selectedTask.title}
                  onChange={(e) => setSelectedTask({ ...selectedTask, title: e.target.value })}
                />
              </div>
              <div>
                <Label>Описание</Label>
                <Textarea
                  value={selectedTask.description || ""}
                  onChange={(e) => setSelectedTask({ ...selectedTask, description: e.target.value })}
                  rows={3}
                />
              </div>
              
              {/* Assignees */}
              {selectedTask.assignees?.length > 0 && (
                <div>
                  <Label>Исполнители</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {selectedTask.assignees.map((assignee, idx) => (
                      <Badge key={idx} variant="secondary">{assignee}</Badge>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-2">
                <Button onClick={handleEditTask} className="flex-1">
                  Сохранить
                </Button>
                <Button variant="outline" onClick={() => openTransferModal(selectedTask)}>
                  <ArrowRight className="w-4 h-4 mr-1" />
                  Передать
                </Button>
              </div>
              
              {/* Comments Section */}
              <div className="border-t pt-4">
                <Label className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Комментарии ({selectedTask.comments?.length || 0})
                </Label>
                
                {/* Comments List */}
                <div className="mt-3 space-y-3 max-h-48 overflow-y-auto">
                  {selectedTask.comments?.length > 0 ? (
                    selectedTask.comments.map((comment) => (
                      <div 
                        key={comment.id} 
                        className={`p-3 rounded-lg ${comment.isTransfer ? 'bg-yellow-50 border border-yellow-200' : 'bg-slate-50'}`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <User className="w-3 h-3 text-slate-400" />
                          <span className="text-sm font-medium text-slate-700">{comment.author}</span>
                          <span className="text-xs text-slate-400">
                            {new Date(comment.created_at).toLocaleString('ru-RU')}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600">{comment.text}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-sm text-slate-400 text-center py-4">Нет комментариев</p>
                  )}
                </div>
                
                {/* Add Comment */}
                <div className="flex gap-2 mt-3">
                  <Input
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Написать комментарий..."
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleAddComment();
                      }
                    }}
                  />
                  <Button onClick={handleAddComment} size="sm">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transfer Task Modal */}
      <Dialog open={showTransferModal} onOpenChange={setShowTransferModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Передать задачу</DialogTitle>
          </DialogHeader>
          {selectedTask && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-800">{selectedTask.title}</p>
              </div>
              
              <div>
                <Label>Передать пользователю</Label>
                <select
                  value={transferToUser}
                  onChange={(e) => setTransferToUser(e.target.value)}
                  className="w-full p-2 border rounded-lg mt-1"
                >
                  <option value="">Выберите пользователя...</option>
                  {users.filter(u => u.username !== currentUser).map(user => (
                    <option key={user.username} value={user.username}>{user.username}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <Label>Причина передачи</Label>
                <Textarea
                  value={transferReason}
                  onChange={(e) => setTransferReason(e.target.value)}
                  placeholder="Укажите причину передачи задачи..."
                  rows={3}
                />
              </div>
              
              <div className="flex gap-2">
                <Button onClick={handleTransferTask} className="flex-1">
                  <ArrowRight className="w-4 h-4 mr-1" />
                  Передать
                </Button>
                <Button variant="outline" onClick={() => setShowTransferModal(false)}>
                  Отмена
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
